package ai.wagemix.net.models.firebase

import com.fsryan.foundation.models.FSIntEnumerated
import com.fsryan.foundation.models.FSStringEnumerated
import kotlinx.serialization.Serializable
import kotlin.time.Instant

@Serializable
data class StripeCustomerSessionResponse(
    val error: String? = null,
    val clientSecret: String? = null
)

@Serializable
data class CreateStripeBillingPortalSessionRequest(val returnEnv: String, val path: String?)

@Serializable
data class StripeBillingPortalSessionResponse(val error: String? = null, val url: String? = null)

enum class WMUserStatus(override val stringValue: String): FSStringEnumerated {
    Active("active"),
    Created("created"),
    Inactive("inactive")
}

enum class WMSubscriptionType(
    override val intValue: Int,
    override val stringValue: String
): FSStringEnumerated, FSIntEnumerated {
    Basic(intValue = 10, stringValue = "basic"),
    None(intValue = 0, stringValue = "none"),
    Pro(intValue = 100, stringValue = "pro");
}

/**
 * The plan a user may use now. A lapsed subscription ([WMUserStatus.Inactive]) keeps the plan it lapsed from in
 * `subscriptionType`, so the site knows what to renew, but grants nothing until it is renewed: every paid-feature
 * check reads this, never `subscriptionType` alone.
 */
fun entitledPlanOf(status: WMUserStatus?, plan: WMSubscriptionType?): WMSubscriptionType {
    return if (status == WMUserStatus.Inactive) WMSubscriptionType.None else plan ?: WMSubscriptionType.None
}

enum class WMUserRole(override val stringValue: String): FSStringEnumerated {
    Admin("admin"),
    User("user"),
}

interface WMUserData {
    val deleted: Boolean
    val email: String
    val firstName: String?
    val lastName: String?
    val role: WMUserRole
    val stripeCustomerId: String?
    val status: WMUserStatus
    val subscriptionType: WMSubscriptionType
    val updatedAt: Instant

    companion object
}

fun WMUserData.Companion.of(
    deleted: Boolean,
    email: String,
    firstName: String?,
    lastName: String?,
    role: WMUserRole,
    stripeCustomerId: String?,
    status: WMUserStatus,
    subscriptionType: WMSubscriptionType,
    updatedAt: Instant
): WMUserData = WMUserDataData(deleted, email, firstName, lastName, role, stripeCustomerId, status, subscriptionType, updatedAt)

private data class WMUserDataData(
    override val deleted: Boolean,
    override val email: String,
    override val firstName: String?,
    override val lastName: String?,
    override val role: WMUserRole,
    override val stripeCustomerId: String?,
    override val status: WMUserStatus,
    override val subscriptionType: WMSubscriptionType,
    override val updatedAt: Instant
): WMUserData